import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { debounce } from 'lodash'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { FiltersContainer as Filters, SearchFilter } from '../../components/filters'
import { TemplateListSlice } from '../../reducers'
import { routes } from '../../routes'
import { useStyles } from './style'
import TemplateItem from './template-item/template-item'

const TemplateList = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const initialFilters = React.useMemo(() => ({ search: '' }), [])
  const [filters, setFilters] = React.useState(initialFilters)

  const templateList = useSelector(TemplateListSlice.selectors.selectTemplateList)
  const templateTotal = useSelector(TemplateListSlice.selectors.selectTotal)
  const [filteredTemplates, setFilteredTemplates] = React.useState(templateList)
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false)
  const [isSearching, setIsSearching] = React.useState(false)
  const isFirstSearchRun = React.useRef(true)

  const [getTemplateList, addTemplateList] = useEvent([TemplateListSlice.eventBus.getTemplateList, TemplateListSlice.eventBus.addTemplateList])

  // Track when initial load completes to prevent flickering
  useEffect(() => {
    if (getTemplateList.hasBeenTriggered && !getTemplateList.isWorking && !initialLoadComplete) {
      setInitialLoadComplete(true)
    }
  }, [getTemplateList.hasBeenTriggered, getTemplateList.isWorking, initialLoadComplete])

  const isLoading = React.useMemo(() => {
    return getTemplateList.isWorking || addTemplateList.isWorking || !initialLoadComplete || isSearching
  }, [getTemplateList.isWorking, addTemplateList.isWorking, initialLoadComplete, isSearching])

  // Initialize filtered templates when templateList loads
  useEffect(() => {
    if (templateList.length > 0) {
      setFilteredTemplates(templateList)
    }
  }, [templateList])

  // Recursive function to check if a template or any of its children match the search term
  const templateMatchesSearch = React.useCallback((template, searchTerm) => {
    const name = template.name?.toLowerCase() || ''

    // Check if current template matches
    if (name.includes(searchTerm)) {
      return true
    }

    // Recursively check children
    if (template.children && template.children.length > 0) {
      return template.children.some(child => templateMatchesSearch(child, searchTerm))
    }

    return false
  }, [])

  // Recursive function to filter children and preserve hierarchy
  const filterTemplateWithChildren = React.useCallback((template, searchTerm) => {
    const name = template.name?.toLowerCase() || ''
    const currentMatches = name.includes(searchTerm)

    // Filter children recursively
    let filteredChildren = []
    if (template.children && template.children.length > 0) {
      filteredChildren = template.children
        .filter(child => templateMatchesSearch(child, searchTerm))
        .map(child => filterTemplateWithChildren(child, searchTerm))
    }

    // If current template matches or has matching children, include it
    if (currentMatches || filteredChildren.length > 0) {
      return {
        ...template,
        children: filteredChildren
      }
    }

    return null
  }, [templateMatchesSearch])

  // Debounced search that filters the cached templateList
  const debouncedSearch = React.useMemo(() => debounce((searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      setFilteredTemplates(templateList)
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase()
      const filtered = templateList
        .filter(template => templateMatchesSearch(template, lowerSearchTerm))
        .map(template => filterTemplateWithChildren(template, lowerSearchTerm))
        .filter(template => template !== null)
      setFilteredTemplates(filtered)
    }
    setIsSearching(false)
  }, 1000), [templateList, templateMatchesSearch, filterTemplateWithChildren])

  useEffect(() => {
    if (initialLoadComplete) {
      // Skip setting isSearching on the first run after initial load completes
      if (isFirstSearchRun.current) {
        isFirstSearchRun.current = false
        debouncedSearch(filters.search)
      } else {
        setIsSearching(true)
        debouncedSearch(filters.search)
      }
    }
  }, [debouncedSearch, filters.search, initialLoadComplete])

  useEffect(() => {
    if (!getTemplateList.hasBeenTriggered) {
      getTemplateList.trigger({ buildHierarchy: true })
    }
  }, [getTemplateList])

  useEffect(() => () => dispatch(TemplateListSlice.actions.resetTemplateList()), [dispatch])

  const handleClearFilters = React.useCallback(() => setFilters(initialFilters), [setFilters, initialFilters])

  const handleTemplateClick = React.useCallback(template => {
    navigate(generatePath(routes.templateEdit.path, { id: template.sk }))
  }, [navigate])

  const handleTemplateKeyPress = React.useCallback((e, template) => e.which === 13 && handleTemplateClick(template), [handleTemplateClick])

  const handleNewTemplate = React.useCallback(() => {
    navigate(generatePath(routes.templateEdit.path, { id: 'new' }))
  }, [navigate])

  const handleUseTemplate = React.useCallback(parentTemplate => {
    navigate(generatePath(routes.templateEdit.path, { id: 'new' }), { state: { parentTemplate } })
  }, [navigate])

  // Recursive function to count all templates including children
  const countTemplates = React.useCallback((templates) => {
    return templates.reduce((total, template) => {
      let count = 1 // Count the template itself
      if (template.children && template.children.length > 0) {
        count += countTemplates(template.children) // Add children count
      }
      return total + count
    }, 0)
  }, [])

  const displayTotal = filters.search ? countTemplates(filteredTemplates) : templateTotal

  return (
    <>
      <Grid container classes={{ root: classes.header }}>
        <Grid container direction='row' alignItems='center'>
          <Grid item style={{ flexGrow: 1 }}>
            <Typography id='template-list-title' tabIndex={0} component='div' variant='h5'>
              Templates
              {!!displayTotal && <Typography component='span' variant='subtitle1' className={classes.total}> {`(${displayTotal})`} </Typography>}
            </Typography>
          </Grid>
          <Filters filters={filters} onChange={setFilters} onClear={handleClearFilters}>
            <SearchFilter name='search' />
          </Filters>
          <Grid item>
            <ActionButton
              id='create-template-button'
              variant='primary'
              name='create-content'
              aria-label='create new content'
              onClick={handleNewTemplate}
            > New Template
            </ActionButton>
          </Grid>
        </Grid>
      </Grid>

      <Paper
        id='template-list'
        classes={{ root: classes.tablePaper }}
        tabIndex={0}
        aria-label='Template List'
        elevation={0}
      >
        <Grid
          id='template-list-headers'
          container
          tabIndex={0}
          direction='row'
          alignItems='center'
          className={classes.gridHeader}
          classes={{ root: classes.tableHead }}
          aria-label='List headers, Title, Type, Last update'
        >
          <Grid item md={1} />
          <Grid item md={3}>Name</Grid>
          <Grid item md={3}>Child Template</Grid>
          <Grid item md={2}>Last update</Grid>
          <Grid item md={2} />
          <Grid item md={1} />
        </Grid>
        {!isLoading && filteredTemplates.map((row, key) => (
          <TemplateItem
            key={key}
            index={key}
            template={row}
            lastOne={key === filteredTemplates.length - 1}
            onClick={handleTemplateClick}
            onNewTemplate={handleUseTemplate}
            onKeyPress={handleTemplateKeyPress}
          />
        ))}
        {isLoading && (
          <Grid
            id='template-list-loading'
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            classes={{ root: classes.loadingAnimation }}
          >
            <CircularProgress size={60} color='primary' />
            <Typography variant='body1' style={{ marginTop: '16px' }}>
              {isSearching ? 'Searching...' : 'Loading...'}
            </Typography>
          </Grid>
        )}
        {!isLoading && !filteredTemplates.length && (
          <Grid
            id='template-list-empty'
            container
            direction='row'
            justifyContent='center'
            alignContent='center'
            classes={{ root: classes.emptyList }}
          >
            <Typography variant='h5'> No template found! </Typography>
          </Grid>
        )}
      </Paper>
    </>
  )
}

export default TemplateList
